<template>
  <ValidationObserver
    ref="sendTemplateForm"
    v-slot="{ valid, pristine }">
    <b-modal
      id="notification-send-modal"
      ref="notificationModal"
      class="static-modal"
      :modal-class="{'fit-spinner': !templateMessagesLoaded, preloading: !templateMessagesLoaded, 'hide-send': !hasApprovedTemplateMessages }"
      title-class="flex flex-align-center"
      centered
      hide-header-close
      :hide-footer="!templateMessagesLoaded"
      :hide-header="!templateMessagesLoaded"
      :static="true"
      size="xl"
      ok-title="Send"
      :ok-disabled="(hasFieldsToPopulate && pristine) || !valid"
      :lazy="true"
      no-close-on-backdrop
      @shown="onShow"
      @hidden="resetData"
      @ok="submit"
      @cancel="cancel" >
      <!-- Modal Title -->
      <template slot="modal-title">
        <img
          src="~@/assets/img/wa-logo-small.png"
          alt="whatsapp logo"
          width="26"
          style="margin-right: 2px;">
        Send Message Template
      </template>

      <!-- Spinner shown while template data is being loaded. -->
      <div
        v-if="!templateMessagesLoaded"
        class="spinner-wrapper">
        <Spinner
          :size="20"
          :thickness="2"/>
      </div>

      <!-- Modal Content -->
      <div
        v-else-if="hasApprovedTemplateMessages"
        class="preview-body flex">
        <!-- style="transform: translate(0, 0);" -->
        <div
          class="notification-form greedy" >
          <div
            ref="formScroll"
            class="notification-send-form" >
            <div>
              <!-- TODO: CONTACT select -->

              <!-- TEMPLATE multiselect -->
              <b-form-group>
                <label
                  class="col-form-label pt-0">
                  Template
                </label>
                <multiselect
                  ref="templateMultiselectRef"
                  v-model="selectedTemplate"
                  placeholder="Select Template"
                  class="flex-options hide-tags no-selected-background"
                  :class="{empty: !templateKeywords.length}"
                  :options="templateKeywords"
                  :preselect-first="true"
                  :multiple="false"
                  :searchable="true"
                  :show-labels="false"
                  :show-no-results="false"
                  :allow-empty="false"
                  @input="changeTemplate()"
                  @open="setPosition">
                  <template slot="noOptions" >
                    <span class="hide" />
                  </template>
                  <template
                    slot="beforeList">
                    <div
                      class="flex flex-align-center multiselect-before"
                      @click="showCreateNewMessageTemplateModal" >
                      <SBIcon
                        :icon="SBIconList.PLUS"
                        :color="SBColors.blackUltralight"
                        :size="9"/>
                      <span class="ellipsis">
                        Create Message Template
                      </span>
                    </div>
                  </template>
                  <template
                    slot="singleLabel"
                    slot-scope="{ option }">
                    {{ option }}
                  </template>
                  <template
                    slot="option"
                    slot-scope="props">
                    <SBIcon
                      :icon="SBIconList.DONE_TICK"
                      :color="SBColors.bluePrimary"
                      :size="9"/>
                    <span class="ellipsis">
                      {{ props.option }}
                    </span>
                  </template>
                  <template
                    v-if="!templateMessagesLoaded"
                    slot="afterList"
                    class="multiselect-title">
                    <div
                      v-if="!tagsMeta || tagsMeta.current_page !== tagsMeta.last_page"
                      v-observe-visibility="hitBottomTemplateList">
                      <Spinner
                        class="mt-2 mb-2"
                        :size="17"
                        :thickness="2" />
                    </div>
                  </template>
                </multiselect>
              </b-form-group>

              <!-- TAGS -->
              <template v-if="selectedTemplateLanguageVersion">
                <b-form-group
                  v-for="(tag, index) in selectedTemplateLanguageVersion.tags"
                  :key="tag">
                  <label>Placeholder {{ tag }}</label>
                  <ValidationProvider
                    :ref="tag"
                    v-slot="{ errors }"
                    :name="`Placeholder ${tag}`"
                    rules="required">
                    <SBTextInput
                      :ref="`tag-${index}`"
                      v-model="tagsToSend[tag]"
                      :focus-shadow="true"
                      :show-tags-select="true"
                      :enable-format-buttons="false"
                      :enable-emoji="false"
                      placeholder="Enter value"
                      :slim="true"
                      @tab="tabPressed(`tag-${index}`)" />
                    <div
                      v-if="errors[0]"
                      class="error flex flex-align-center">
                      <SBIcon
                        :icon="SBIconList.ERROR"
                        :color="SBColors.redPrimary"
                        :size="12" />
                      <span>{{ errors[0] }}</span>
                    </div>
                  </ValidationProvider>
                </b-form-group>
              </template>

              <!-- Dynamic link TAGS -->
              <template v-if="selectedTemplateLanguageVersion">
                <b-form-group
                  v-for="(tag, index) in selectedTemplateLanguageVersion.button_tags"
                  :key="index">
                  <label class="flex flex-align-center">
                    Dynamic link extension
                    <SBIcon
                      id="dynamic-link-info"
                      class="ml-1"
                      :icon="SBIconList.INFORMATION"
                      :color="SBColors.greyPrimary"
                      :size="12" />
                    <b-popover
                      target="dynamic-link-info"
                      custom-class=""
                      boundary="window"
                      triggers="hover"
                      placement="top">
                      <div>
                        <h1>Dynamic link extension</h1>
                        <p>Please enter the desired link extension that will appear at the end of the link. For example: If you enter “products”, the resulting link will be "{{ linkExample }}/products".</p>
                      </div>
                    </b-popover>
                  </label>
                  <ValidationProvider
                    :ref="`button-tag-${index}`"
                    v-slot="{ errors }"
                    name="Dynamic link extension"
                    rules="required">
                    <b-form-input
                      :ref="`button-tag-input-${index}`"
                      v-model="buttonTagsToSend[tag.name]"
                      placeholder="Enter value"
                      @input="dynamicLinkChange" />
                    <div
                      v-if="errors[0]"
                      class="error flex flex-align-center">
                      <SBIcon
                        :icon="SBIconList.ERROR"
                        :color="SBColors.redPrimary"
                        :size="12" />
                      <span>{{ errors[0] }}</span>
                    </div>
                  </ValidationProvider>
                </b-form-group>
              </template>

              <!-- FILE ATTACHMENT -->
              <template
                v-if="selectedTemplateLanguageVersion && selectedTemplateLanguageVersion.attachment">
                <b-form-group>
                  <label
                    class="col-form-label p-0 m-0">
                    <span class="capitalize">{{ selectedTemplateLanguageVersion.attachment }}</span>
                  </label>
                  <ValidationProvider
                    v-slot="{ errors }"
                    name="Attachment"
                    rules="required" >
                    <DropZone
                      ref="dropZone"
                      v-model="files"
                      :placeholder="documentPlaceholder"
                      :extensions="allowedTemplateMessageAttachments(selectedTemplateLanguageVersion.attachment)"
                      @dropped-in="fileDropped"/>
                    <div
                      v-if="errors[0] && !$refs.dropZone.unsupportedFileType"
                      class="error flex flex-align-center">
                      <SBIcon
                        :icon="SBIconList.ERROR"
                        :color="SBColors.redPrimary"
                        :size="12" />
                      <span>{{ errors[0] }}</span>
                    </div>
                  </ValidationProvider>
                </b-form-group>
              </template>
            </div>
          </div>
        </div>
        <MessagePreview :content="messageContent"/>
      </div>

      <InfoCard
        v-else-if="!hasApprovedTemplateMessages"
        :flat-icon="SBIconList.MESSAGE_TEMPLATES_LOGO"
        title="Add WhatsApp Message Templates"
        :description="noTemplatesDescription"
        action-text="New"
        @action="showCreateNewMessageTemplateModal" />
    </b-modal>
  </ValidationObserver>
</template>

<script>
import { mapActions, mapGetters } from 'vuex';
import Multiselect from 'vue-multiselect';
import * as Sentry from '@sentry/browser';
import { debounce } from 'debounce';

import { escapeHtml, unescapeHtml, whatsappFormatMessage, whatsAppFormatToHTML } from '@/helpers/stringHelpers';
import getLanguageName from '@/helpers/getLanguageName';
import Spinner from '../../../common/Spinner';
import DropZone from '../../../common/DropZone';
import SBIcon from '@/components/common/Icon';
import SBIconList from '@/helpers/lists/sb-icon-list';
import SBColors from '@/helpers/lists/colors';
import { allowedTemplateMessageAttachments } from '../../../../helpers/mediaHelpers';
import randomString from '@/helpers/randomString';
import JuneMixin from '@/mixins/June';
import InfoCard from '@/components/common/InfoCard';
import MessagePreview from '@/components/common/MessagePreview';
import SBTextInput from '@/components/business/widgets/SBTextInput';
import { populateCoustomFields } from '@/helpers/quickRepliesParser';

export default {
  name: 'NotificationModal',
  components: {
    Multiselect,
    DropZone,
    SBIcon,
    Spinner,
    InfoCard,
    MessagePreview,
    SBTextInput,
  },
  mixins: [
    JuneMixin,
  ],
  props: {
    contacts: {
      type: Array,
      default: null,
    },
    showContactSelector: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      whatsappPolicyUrl: 'https://www.whatsapp.com/legal/business-policy/',
      files: [],
      isBulk: false,
      bulkIds: {
        contacts: [],
        tags: [],
      },
      formData: new FormData(),
      languageMultiselectHoverState: false,
      languages: [],
      selectedLanguage: null,
      selectedTemplate: null,
      selectedTemplateObject: null,
      selectedTemplateLanguageVersion: null,
      tagsToSend: {},
      buttonTagsToSend: {},
      visible: false,
      tempBackupOfValues: null,
      templateMessageListCombined: [],
      tagsMeta: null,
      SBIconList,
      SBColors,
      messageContent: {},
      formScrollTop: 0,
    };
  },
  computed: {
    ...mapGetters({
      activeConversation: 'chat/activeConversation',
      templateMessageList: 'notification/templateMessageList',
      templateMessagesLoaded: 'notification/templateMessagesLoaded',
      // bulkMessagesSent: 'notification/bulkMessagesSent',
      me: 'organisation/me',
      apiError: 'notification/apiError',
      notificationTags: 'notification/notificationTags',
      notificationTagsLoaded: 'notification/notificationTagsLoaded',
    }),
    noTemplatesDescription() {
      return `Create Message Templates so you can initiate a WhatsApp conversation or respond to a conversation if more than 24 hours have passed from the last message sent by the contact. Visit <a href='${this.whatsappPolicyUrl}' target='_blank'>WhatsApp Business Legal</a> for more information.`;
    },
    /** Checks if there is at least one approved template message. */
    hasApprovedTemplateMessages() {
      return this.templateMessagesLoaded && this.templateKeywords.length;
    },
    /** Computes the message content from the tags data populated by user. */
    message() {
      let selectedTemplateMessage = this.selectedTemplateLanguageVersion ? escapeHtml(this.selectedTemplateLanguageVersion.text.trim()) : '';
      if (this.selectedTemplateLanguageVersion) {
        this.selectedTemplateLanguageVersion.tags.forEach((tag) => {
          if (this.tagsToSend[tag]) {
            const value = this.tagsToSend[tag].replace(/<span>\s*<\/span>/g, '');
            if (value) {
              selectedTemplateMessage = selectedTemplateMessage.replace(new RegExp(`\\{\\{${tag}\\}\\}`, 'g'), value);
            }
          }
        });
        selectedTemplateMessage = selectedTemplateMessage.replace(/{{(.*?)}}/g, '<span class="editor-tag ellipsis">$1</span>');
      }
      return whatsAppFormatToHTML(selectedTemplateMessage);
    },
    linkExample() {
      const link = this.selectedTemplateLanguageVersion.buttons.length ? this.selectedTemplateLanguageVersion.buttons[0].value : '';
      return link.replace(/\/?\{\{\d\}\}$/, '');
    },
    /** Creates a list of approved template names to be populated on the templates multiselect. */
    templateKeywords() {
      // TODO this is probably not needed anymore since we do not refference keyword anywhere on frontend. Refactor this on rewrite
      const templateKeywords = [];
      this.templateMessageListCombined.forEach((template) => {
        if (template.status === 'approved') {
          templateKeywords.push(template.name || template.keyword);
        }
      });

      return templateKeywords;
    },
    hasFieldsToPopulate() {
      return !!(this.selectedTemplateLanguageVersion && (this.selectedTemplateLanguageVersion.tags.length || this.selectedTemplateLanguageVersion.attachment));
    },
    documentPlaceholder() {
      let placeholder = 'Select or drop JPG, JPEG, or PNG here';
      switch (this.selectedTemplateLanguageVersion.attachment) {
        case 'video':
          placeholder = 'Select or drop MP4 here';
          break;
        case 'document':
          placeholder = 'Select or drop PDF here';
          break;
        default:
          break;
      }
      return placeholder;
    },
  },
  watch: {
    /** Closes the modal when conversation is changed. */
    activeConversation() {
      // Cancel template modal if conversation is changed while template modal is opened for previous conversation.
      if (this.visible) {
        this.hide();
      }
    },
    hasApprovedTemplateMessages() {
      // this.$nextTick(() => {
      //   if (!this.$refs.formScroll) {
      //     return;
      //   }
      //   this.$refs.formScroll.onscroll = (() => {
      //     this.trackScroll();
      //   });
      // });
    },
    /** Sets the selected template object and resets the tags input on template change. */
    selectedTemplate() {
      this.resetTags();
      if (this.selectedTemplate) {
        this.selectedTemplateObject = this.templateMessageList[this.selectedTemplate.replace(/\s/g, '_')];
      }
    },
    /** Sets the available languages for the selected template. */
    selectedTemplateObject() {
      this.selectedLanguage = null;
      // this.languages = this.selectedTemplateObject.map(templateVersion => ({
      //   languageName: getLanguageName(templateVersion.language, false),
      //   languageCode: templateVersion.language,
      // }));
      // select language automatically if only one is available
      if (this.languages.length === 1) {
        [this.selectedLanguage] = this.languages;
      }
    },
    /** Resets tags on language change. */
    // DEPRECATED
    selectedLanguage() {
      this.resetTags();
      if (!this.selectedLanguage) {
        this.selectedTemplateLanguageVersion = null;
      } else {
        this.selectedTemplateLanguageVersion = this.selectedTemplateObject.find(
          template => template.language === this.selectedLanguage.languageCode,
        );
      }
    },
    tagsToSend: {
      handler() {
        this.setModalBackupData();
      },
      deep: true,
    },
    templateMessageList() {
      if (this.tempBackupOfValues && this.tempBackupOfValues.language) {
        const targetTemplate = this.templateMessageList.data.find(template => template.id === this.tempBackupOfValues.language);
        if (targetTemplate && targetTemplate.status === 'approved') {
          this.selectedTemplate = targetTemplate.name || targetTemplate.keyword;
          this.changeTemplate();
          this.$nextTick(() => {
            if (this.tempBackupOfValues.tagValues) {
              this.tagsToSend = { ...this.tempBackupOfValues.tagValues };
            }
          });
        }
      }
      this.templateMessageListCombined = [...this.templateMessageListCombined, ...this.templateMessageList.data];
      this.changeTemplate();
    },
    message() {
      this.setPreview();
    },
  },
  mounted() {
    // if (this.$refs.sendTemplateForm) {
    //   this.$watch(
    //     () => this.$refs.sendTemplateForm.flags,
    //     () => {
    //       this.setModalHeight();
    //     },
    //   );
    // }
    // window.addEventListener('resize', this.resizeAction);
  },
  beforeDestroy() {
    // window.removeEventListener('resize', this.resizeAction);
  },
  methods: {
    ...mapActions({
      fetchTemplateMessages: 'notification/fetchTemplateMessages',
      sendTemplateMessage: 'notification/sendTemplateMessage',
      // countUniqueContacts: 'contact/countUniqueContacts',
      // subscribeToBulkSendStats: 'pusher/subscribeToBulkSendStats',
      // resetUniqueContactCount: 'contact/resetUniqueContactCount',
      loadAvailableNotificationTags: 'notification/loadAvailableNotificationTags',
      addMessageToFastLoad: 'chat/addMessageToFastLoad',
    }),
    allowedTemplateMessageAttachments,
    dynamicLinkChange: debounce(function f() {
      this.setPreview();
    }, '100'),
    // resizeAction() {
    //   this.setModalHeight();
    //   const elements = document.querySelectorAll('#notification-send-modal .notification-send-form .multiselect__content-wrapper');
    //   for (let i = 0; i < elements.length; i++) {
    //     delete elements[i].dataset.initialTop;
    //     elements[i].style.top = 'unset';
    //   }
    //   this.trackScroll();
    // },
    // trackScroll() {
    //   this.formScrollTop = this.$refs.formScroll.scrollTop;
    //   this.setPosition();
    // },
    // trackScrollModalBody() {
    //   this.formScrollTop = document.querySelectorAll('#notification-send-modal .modal-body')[0].scrollTop;
    //   this.setPosition();
    // },
    setPosition(reset = false) {
      // this.$nextTick(() => {
      //   const elements = document.querySelectorAll('#notification-send-modal .notification-send-form .multiselect__content-wrapper');
      //   for (let i = 0; i < elements.length; i++) {
      //     const elemRect = elements[i].getBoundingClientRect();
      //     const elemParentRect = elements[i].parentElement.getBoundingClientRect();

      //     // Close dropdown when it gets out of view
      //     if (
      //       ((elements[i].parentElement.offsetTop + elemParentRect.height) < this.formScrollTop) ||
      //       ((elements[i].parentElement.offsetTop + elemParentRect.height) > (this.formScrollTop + this.$refs.formScroll.offsetHeight))
      //     ) {
      //       this.$refs.templateMultiselectRef.deactivate();
      //     }

      //     if (elemRect.height && !elements[i].dataset.initialTop) {
      //       elements[i].dataset.initialTop = elements[i].parentElement.offsetTop + elemParentRect.height;
      //     }
      //     if (elemRect.height) {
      //       elements[i].style.top = `${elements[i].dataset.initialTop - this.formScrollTop}px`;
      //     }
      //   }
      // });
    },
    changeTemplate() {
      this.tagsToSend = {};
      this.buttonTagsToSend = {};
      this.files = [];
      this.formData.delete('file');
      if (this.$refs.dropZone) {
        this.$refs.dropZone.clearFiles();
      }
      this.selectedTemplateLanguageVersion = this.templateMessageListCombined.find(template => template.name === this.selectedTemplate || template.keyword === this.selectedTemplate);
      this.setModalBackupData();
      this.setPreview();
      // this.setModalHeight();
    },
    setPreview() {
      if (!this.selectedTemplateLanguageVersion) {
        return;
      }
      this.messageContent.text = populateCoustomFields(whatsappFormatMessage(this.message, true))
        .replace(/{!{(.*?)}!}/g, '<span class="editor-tag ellipsis">$1</span>');
      this.messageContent.attachmentType = this.selectedTemplateLanguageVersion.attachment;
      this.messageContent.attachment = this.formData.has('file') ? this.formData.get('file') : '';
      // Buttons
      this.messageContent.buttons = [];
      if (this.selectedTemplateLanguageVersion.buttons && this.selectedTemplateLanguageVersion.buttons.length) {
        this.selectedTemplateLanguageVersion.buttons.forEach((b) => {
          let value = b.value;
          if (this.selectedTemplateLanguageVersion.button_tags) {
            this.selectedTemplateLanguageVersion.button_tags.forEach((tag) => {
              if (this.buttonTagsToSend[tag.name]) {
                value = value.replace(new RegExp(`\\{\\{${tag.name}\\}\\}`, 'g'), `{{${this.buttonTagsToSend[tag.name]}}}`);
              }
            });
          }

          this.messageContent.buttons.push({
            value,
            title: b.title,
            icon: b.icon,
            type: b.type,
          });
        });
      }
      this.$nextTick(() => {
        this.messageContent = { ...this.messageContent };
      });
    },
    /** We are doing this manually because modal does not play nice with scrolling content and elements escaping overflow. */
    // setModalHeight() {
    //   this.$nextTick(() => {
    //     if (!document.querySelectorAll('#notification-send-modal .preview-body')[0]) {
    //       return;
    //     }
    //     document.querySelectorAll('#notification-send-modal .preview-body')[0].style.height = '0px';
    //     const formHeight = document.querySelectorAll('#notification-send-modal .notification-send-form')[0].scrollHeight;
    //     const previewHeight = document.querySelectorAll('#notification-send-modal .preview')[0].scrollHeight;

    //     // -195 (margin, header and footer combined)
    //     const maxAllowed = window.innerHeight - 195;

    //     const targetHeight = Math.min(maxAllowed, Math.max(formHeight, previewHeight));
    //     const targetHeightNarrow = Math.min(maxAllowed, formHeight + previewHeight + 1);
    //     document.querySelectorAll('#notification-send-modal .preview-body')[0].style.height = `${targetHeight}px`;
    //     document.querySelectorAll('#notification-send-modal .modal-body')[0].style.height = `${targetHeightNarrow}px`;
    //   });
    // },
    /** Loads more templates if dropdown is visible and if scroll is not active. */
    checkIfTemplatesListNeedsMore() {
      if (!this.$refs.templateMultiselectRef) {
        if (!this.hasApprovedTemplateMessages) {
          this.loadTemplates(true);
        }
        return;
      }
      const targetMultiselectContent = this.$refs.templateMultiselectRef.$el.getElementsByClassName('multiselect__content-wrapper');
      if (!this.hasApprovedTemplateMessages || (targetMultiselectContent[0] && targetMultiselectContent[0].clientHeight !== 0 && targetMultiselectContent[0].scrollHeight === targetMultiselectContent[0].clientHeight)) {
        this.loadTemplates(true);
      }
    },
    /** Handles file drop on the file input. */
    fileDropped(file) {
      if (file && file.length) {
        this.formData.set('file', file[0]);
      } else {
        this.formData.delete('file');
      }
      this.setPreview();
    },
    onShow() {
      this.visible = true;
      // document.querySelectorAll('#notification-send-modal .modal-body')[0].onscroll = (() => {
      //   this.trackScrollModalBody();
      // });
    },
    onHide() {
      this.visible = false;
    },
    hitBottomTemplateList(reached) {
      if (reached) {
        this.loadTemplates(true);
      }
    },
    /** Resets the data on the template form. */
    resetData() {
      this.visible = false;
      this.resetTags();
      this.selectedTemplate = null;
      this.bulkIds = {
        contacts: [],
        tags: [],
      };
      this.templateMessageListCombined = [];
      this.tagsMeta = null;
      this.$refs.sendTemplateForm.reset();
    },
    /** Resets tags. */
    resetTags() {
      this.tagsToSend = {};
      this.$refs.sendTemplateForm.reset();
    },
    /** Brings up the moda, fetches the templates and sets the recepient. */
    show(bulk = false) {
      this.visible = true;
      this.$nextTick(() => {
        this.tempBackupOfValues = { ...this.activeConversation.openSendNotification };
        this.isBulk = bulk;
        if (!bulk) {
          const targetContact = this.activeConversation.contact && this.activeConversation.contact.id ? this.activeConversation.contact.id : this.activeConversation.phone;
          this.bulkIds.contacts.push(targetContact);
        }
        this.$refs.notificationModal.show();
        this.loadTemplates();
        // this.loadAvailableNotificationTags(this.activeConversation.businessId || this.activeConversation.business_id);
      });
    },
    hide() {
      this.$refs.notificationModal.hide();
    },
    loadTemplates(background = false) {
      if (this.tagsMeta && this.tagsMeta.current_page >= this.tagsMeta.last_page) {
        return;
      }
      const data = {
        limit: 255,
        order: 'name',
        status: 'approved',
        page: this.tagsMeta ? this.tagsMeta.current_page + 1 : 1,
      };
      this.fetchTemplateMessages({ businessId: this.activeConversation.businessId || this.activeConversation.business_id, data, background })
        .then((response) => {
          this.tagsMeta = response.data.meta;
          this.checkIfTemplatesListNeedsMore();
        });
    },
    /** Submits new template message to thw user. */
    submit() {
      this.$refs.sendTemplateForm.validate();
      if (this.$refs.sendTemplateForm.flags.valid) {
        const sendData = {};
        Object.keys(this.tagsToSend).forEach((tag) => {
          sendData[tag] = whatsappFormatMessage(this.tagsToSend[tag])
            .replace(/!!!(.*?)!!!/g, '{{$1}}')
            .replace('{{contact name}}', '{{contact_name}}')
            .replace('{{number name}}', '{{business_name}}');

          const customFieldMatch = new RegExp(/{{(.*?)}}/, 'g');
          const matches = sendData[tag].match(customFieldMatch) || [];
          matches.forEach((m) => {
            const noSpace = m.replaceAll(' ', '_');
            sendData[tag] = sendData[tag].replace(m, noSpace);
          });
        });

        const tempSid = randomString();

        this.formData.set('keyword', this.selectedTemplateLanguageVersion.keyword);
        this.formData.set('language', this.selectedTemplateLanguageVersion.language);
        this.formData.set('tags', JSON.stringify(sendData));
        this.formData.set('temp_sid', tempSid);
        if (!this.activeConversation.contact && this.activeConversation.phone) {
          this.formData.set('number', this.activeConversation.phone);
        } else {
          this.formData.set('bulk', JSON.stringify(this.bulkIds));
          // Sentry.captureException(`Bulk - ${JSON.stringify(this.bulkIds)}`);
        }
        // tags for dynamic link buttons
        if (Object.keys(this.buttonTagsToSend).length) {
          this.formData.set('button_tags', JSON.stringify(this.buttonTagsToSend));
        }
        const payload = {
          merchant_id: this.activeConversation.business_id || this.activeConversation.businessId,
          conversation_id: this.activeConversation.id,
          data: this.formData,
        };

        const URLObj = window.URL || window.webkitURL;
        let mediaUrl = null;
        if (this.files && this.files[0] && this.files[0].type && (this.files[0].type.startsWith('image') || this.files[0].type.startsWith('video'))) {
          mediaUrl = URLObj.createObjectURL(this.files[0]);
        } else if (this.files && this.files[0] && this.files[0].name) {
          mediaUrl = this.files[0].name;
        }

        const buttons = [];
        if (this.selectedTemplateLanguageVersion.buttons && this.selectedTemplateLanguageVersion.buttons.length) {
          this.selectedTemplateLanguageVersion.buttons.forEach((b, index) => {
            let value = b.value;
            if (this.selectedTemplateLanguageVersion.button_tags) {
              this.selectedTemplateLanguageVersion.button_tags.forEach((tag) => {
                if (this.buttonTagsToSend[tag.name]) {
                  value = value.replace(new RegExp(`\\{\\{${tag.name}\\}\\}`, 'g'), this.buttonTagsToSend[tag.name]);
                }
              });
            }

            buttons.push({
              index,
              value,
              title: b.title,
              icon: b.icon,
              type: b.type,
            });
          });
        }

        // TODO Make it work as "assume success"
        // 1. When send is clicked initiate send and show message on the messages list
        // 2. if message is sent everything stays as is
        // 3. If message send fails it will be updated on the messages list
        // 4. if the request itself fails then show the message to user, remove it from message list and show the prepopulated modal again.
        const outboundMessageData = {
          sid: tempSid,
          body: unescapeHtml(populateCoustomFields(whatsappFormatMessage(this.message))), // TODO test this with template that has something bolded
          media_type: this.files && this.files[0] && this.files[0].type ? this.files[0].type : null,
          media_url: mediaUrl,
          loaded: true,
          direction: 'outbound',
          status: 'sending',
          timestamp: this.$moment().unix(),
          agent: true,
          sender: this.me.name,
          sender_icon: this.me.platform_id,
          message_type: 'Message Template',
        };
        if (buttons.length) {
          outboundMessageData.buttons = {
            items: buttons,
          };
        }
        this.$eventBus.$emit('addoutboundmessage', outboundMessageData);
        this.addMessageToFastLoad({
          message: outboundMessageData,
          conversation_uid: this.activeConversation.id,
        });

        const previousUserId = this.activeConversation.user_id;

        this.$eventBus.$emit('template-message-assign', {
          conversationId: payload.conversation_id,
        });

        this.sendTemplateMessage(payload)
          .then((response) => {
          //   NOTE: We decided not to wait for the template to be sent to show the conversation but to show the fake real conversation and then swap it when the conversation gets created. Check ConversationTmp for further logic.
          //   if (response.status === 200 && !this.activeConversation.contact && this.activeConversation.phone) {
          //     this.$eventBus.$emit('template-message-sent', {
          //       phone: this.activeConversation.phone,
          //       message: outboundMessageData.body,
          //       direction: outboundMessageData.direction,
          //       sent_at: outboundMessageData.sent_at,
          // });
            // }
          })
          .catch((error) => {
            this.$eventBus.$emit('template-message-assign-failed', {
              conversationId: payload.conversation_id,
              previousUserId,
            });
            if (this.isNetworkError(error)) {
              this.$eventBus.$emit('removeoutboundmessage', { sid: outboundMessageData.sid, conversation: this.activeConversation.id });
              this.$eventBus.$emit('pillmessage', 'Sending template message failed');
            }
          });

        this.activeConversation.openSendNotification = false;

        if (!this.activeConversation.contact && this.activeConversation.phone) {
          this.$eventBus.$emit('template-message-sent', {
            phone: this.activeConversation.phone,
            message: outboundMessageData.body,
            direction: outboundMessageData.direction,
            timestamp: outboundMessageData.timestamp,
          });
        }

        // this.$eventBus.$emit('set-temp-message', outboundMessageData);
        this.$eventBus.$emit('change-new-temp-conversation-id', tempSid);
        this.juneTrack('Send template message', { template_name: this.selectedTemplateLanguageVersion.keyword });
      }
    },
    cancel() {
      this.activeConversation.openSendNotification = false;
    },
    /**
     * Checks if the cursor is above the multiselect select or tags section and only shows the hover state in that case.
     * It had to be done like that because setting the :hover on multiselect also trigers it if the cursor is above the list and that is not how we want it to be shown.
     *
     * @param event Mouse event
     */
    addHover(event) {
      event.path.some((element) => {
        this.languageMultiselectHoverState = element.className.indexOf('multiselect__select') >= 0 || element.className.indexOf('multiselect__tags') >= 0;
        return this.languageMultiselectHoverState || element.className.indexOf('multiselect') === -1;
      });
    },
    /**
     * Removes the hover state set by the addHover method.
     */
    removeHover() {
      this.languageMultiselectHoverState = false;
    },
    setModalBackupData() {
      if (!this.visible) {
        return;
      }
      const backupValues = {
        language: this.selectedTemplateLanguageVersion ? this.selectedTemplateLanguageVersion.id : '',
      };
      const backupTagValues = {};
      Object.keys(this.tagsToSend).forEach((tag) => {
        backupTagValues[tag] = this.tagsToSend[tag];
      });
      backupValues.tagValues = { ...backupTagValues };
      this.activeConversation.openSendNotification = { ...backupValues };
    },
    showCreateNewMessageTemplateModal() {
      if (this.$refs.templateMultiselectRef) {
        const allOptions = this.$refs.templateMultiselectRef.$el.getElementsByClassName('multiselect__element');
        let targetOption;
        const proccessedSelectedTemplateName = this.selectedTemplate || '';
        for (let i = 0; i < allOptions.length; i++) {
          if (allOptions[i].innerText.trim() === proccessedSelectedTemplateName) {
            targetOption = allOptions[i];
            break;
          }
        }
        if (targetOption && targetOption.children[0]) {
          targetOption.children[0].click();
        }
      }

      this.$emit('create-new-template');
    },
    tabPressed(index) {
      const patern = /(\w+)-(\d)/g;
      const [, type, pos] = patern.exec(index);
      if (type && type === 'tag' && pos >= 0) {
        if (this.$refs[`tag-${Number(pos, 10) + 1}`] && this.$refs[`tag-${Number(pos, 10) + 1}`][0]) {
          const targetEl = this.$refs[`tag-${Number(pos, 10) + 1}`][0].$el.querySelector('.ql-editor');
          if (targetEl) {
            targetEl.focus();
          }
        } else if (this.$refs['button-tag-input-0'] && this.$refs['button-tag-input-0'][0]) { // try to find dyn link
          this.$refs['button-tag-input-0'][0].$el.focus();
        }
      }
    },
  },
};
</script>

<style lang="scss" scoped>
::v-deep #notification-send-modal {
  .modal-body {
    padding: 0 !important;
  }

  &.preloading .modal-body {
    background: $white !important;
    border-radius: 5px;
  }

  &.modal:not(.fit-width) .modal-dialog {
    max-width: 780px !important;
    width: calc(100% - 16px) !important;
  }

  &.modal.fit-spinner .modal-dialog {
    width: 100px !important;

    .modal-body {
      height: 100px !important;
      overflow: hidden;

      .spinner-wrapper {
        margin-top: 40px;
      }
    }
  }

  .notification-send-form {
    // height: 100%;
    // overflow-y: auto;
    padding: 20px;

    .template-label-wrapper {
      z-index: 200;

      .multiselect {
        display: inline-block;
        max-width: fit-content;
        min-height: 16px !important;
        height: 16px;
        box-shadow: none;

        &:focus {
          box-shadow: unset !important;
        }

        .multiselect__select {
          padding: 0;
          height: 11px;
          width: 11px;
        }

        .multiselect__tags {
          padding: 0;
          min-height: 16px;
          height: 16px;
          border: none;

          .multiselect__single {
            font-size: 14px !important;
            line-height: 1.2 !important;
            color: $greyPrimary !important;
            padding-right: 18px !important;
          }
        }

        &.hover {
          .multiselect__single {
            color: $blackPrimary !important;
          }
          .multiselect__select {
            mask-image: url(~@/assets/img/SB-Icons/down-arrow.svg);
            mask-size: 11px auto !important;
            mask-repeat: no-repeat;
            mask-position: center;
            background-color: $blackPrimary;
          }
        }

        .multiselect__content-wrapper {
          width: 212px;
          right: 0;
          z-index: 101;
          margin-top: 4px;

          .multiselect__content {
            .lang-list-label {
              display: block !important;
              font-size: 12px !important;
              line-height: 1.4 !important;
              padding: 10px 0 3px 12px !important;
              color: $greyPrimary !important;
            }

            .multiselect__element {
              .multiselect__option {
                padding: 5px 12px !important;
                font-size: 14px !important;
                line-height: 1.2 !important;
                color: $blackPrimary !important;
              }
            }
          }
        }
      }
    }
  }

  &.hide-send {
    .modal-body {
      background-color: $white;
    }

    .modal-footer .btn-primary {
      display: none;
    }
  }
}

::v-deep .info-card {
  margin: 20px !important;
  .action .btn {
    margin: 0 !important;
    font-family: HelveticaNeue !important;
    font-size: 14px !important;
    font-weight: 500 !important;
    padding: 6px 13px !important;
    min-width: unset !important;

    .sb-icon .icon.mask {
      background-color: $white !important;
    }
  }
}

::v-deep .multiselect.empty {
  ul.multiselect__content {
    height: 32px;
    overflow: hidden;
  }
}

#notification-send-modal {
  .modal-title .icon {
    top: 2px;
    position: relative;
  }

  .modal-content {
    .modal-body {
      .spinner-wrapper {
        margin: 20px;
      }
    }
  }

  .check-consent {
    display: flex;
    justify-content: center;
    margin-top: 20px;
    .loader {
      position: unset !important;
      width: unset !important;
      margin: unset !important;
      margin-right: 5px !important;
    }
  }

  .contacts-spinner, .clear-contact {
    position: absolute;
    right: 10px;
    left: unset;
    top: 31px;
    width: fit-content;
    margin: 0;
    z-index: 100;
  }

  .clear-contact {
    padding: 8px;
    width: 30px;
    height: 30px;
    right: 6px;
    top: 23px;
  }

  .multiselect, input {
    height: 100%;
    border-radius: 5px;
    box-shadow: 0 1px 3px 0 rgba(48, 54, 70, 0.1);
    &:focus {
      box-shadow: 0 0 0 3px #bfdeff !important;
    }
  }

  input {
    border: solid 1px #cfd8dc !important;
    background-color: #ffffff !important;
    padding: 7px 10px !important;
  }

  .message {
    display: block;
    border-radius: 5px;
    border: solid 1px #cfd8dc;
    background-color: $white;
    padding: 7px 10px;
    color: $blackPrimary;
    font-size: 14px;
    min-height: 32px;
    cursor: pointer;

    ::v-deep {
      p b {
        font-weight: 600 !important;
      }

      p p {
        min-height: 10px;
      }

      .editor-tag {
        position: relative;
        bottom: 1px;
        background-color: #fff;
        padding: 0 8px;
        border-radius: 30px;
        border: 1px solid $blackPrimary;
        font-weight: 500 !important;
        text-transform: capitalize;
        font-size: 12px !important;
      }
    }
  }

  .send-btn {
    display: flex;
    align-items: center;
    margin-top: 22px;

    .loader {
      margin-right: 4px;
    }

    .message-sent {
      display: flex;

      img {
        margin-top: 0;
        margin-bottom: 0;
        margin-right: 5px;
      }
    }
  }

  .main-error, .message-sent {
    margin-top: 22px !important;
  }

  .error {
    color: $redPrimary;
    margin-top: 5px;
    padding: 5px 11px 5px 6px;

    .sb-icon {
      margin-right: 6px;
    }
  }
}

::v-deep .multiselect.no-selected-background .multiselect__content-wrapper .multiselect__content .multiselect__element .multiselect__option--highlight {
  background-color: unset !important;
}

::v-deep .multiselect.no-selected-background .multiselect__content-wrapper .multiselect__content .multiselect__element:hover {
  background-color: $hover !important;
}

::v-deep .multiselect.no-selected-background .multiselect__content-wrapper {
  // position: fixed !important;
  // width: calc(100% - 40px) !important;
  // height: 100%;
}

.multiselect-before {
  color: $blackUltralight;
  padding: 8px 10px;

  .sb-icon {
    margin-right: 15px;
    margin-left: 5px;
  }

  &:hover {
    background-color: $hover;
    color: $bluePrimary;

    ::v-deep .sb-icon .icon.mask {
      background-color: $bluePrimary !important;
    }
  }
}

// .check-consent {
//   display: flex;
//   justify-content: center;
//   margin-top: 20px;
//   .loader {
//     position: unset !important;
//     width: unset !important;
//     margin: unset !important;
//     margin-right: 5px !important;
//   }
// }
</style>

<style lang="scss">
#notification-send-modal {
  .modal-dialog {
    container: form-input / inline-size;
  }
  .preview-body {
    .notification-form {
      min-width: 1%;
    }
  }
}

@container form-input (inline-size >= 625px) {
  .modal-body {
    height: unset !important;
  }
}

@container form-input (inline-size < 625px) {
  #notification-send-modal {
    .modal-content {
      // transform: translate(0px, 0px) !important;
    }
    .modal-body {
      overflow: auto;
    }
    .preview-body {
      flex-direction: column;
      // height: unset !important;

      & > .greedy {
        transform: none !important;
      }

      & > div {
        // height: unset;
        // overflow-y: unset;
      }

      .preview {
        background: $white;
        width: 100%;
        border-left: none;
        border-top: 1px solid #E6EBED;
      }
    }

    .notification-send-form {
      overflow-y: unset !important;
    }

    .multiselect__content-wrapper {
      // margin-top: 67px; // To add up for the new ancor which is above the header.
    }
  }
}
</style>
